import React, { useContext, useRef, useState } from 'react';
import { styled, TextField, Grid } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select/SelectInput';
import { SendOutlined } from '@mui/icons-material';
import { isEmpty } from 'lodash';

import Dropdown from 'Components/Dropdown/index';
import SendEmailActions from './SendEmailActions';

import { useAppSelector } from 'hooks/index';
import { selectUserData } from 'store/user/userSelector';
import validateEmailAddress from 'Utils/validateEmailAddress';
import { useNotificationContext } from 'app/contexts/NotificationContext/index';
import { DropdownOption } from 'Interfaces/Dropdown/SelectDropdownInterfaces';
import UserContext from '../../context/UsersContext';
import { UsersViewTitles } from '../../constant/UsersViewTitles.enum';
import {
    FirestoreUserRole,
    InvitedUser,
    useSendEmailInviteMutation,
} from 'generated/graphql';
import { selectPartnerData, selectTeamsForTeamSelector } from 'store/partner/partnerSelector';

const StyledInputsContainer = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'center',
    justifyItems: 'center',
    flexDirection: 'column',
    alignItems: 'center',
}));

const StyledSingleInputContainer = styled('div')(() => ({
    marginBottom: '24px',
    width: '446px',
}));

const userRolesOptions: DropdownOption[] = [
    {
        label: 'User',
        value: FirestoreUserRole.User,
    },
    {
        label: 'Organization Admin',
        value: FirestoreUserRole.OrganizationAdmin,
    },
];

const SendEmailInvitation: React.FC = () => {
    const emailInputRef = useRef<HTMLInputElement>(null);

    const { showNotification } = useNotificationContext();
    const { setHeader, addInvitation } = useContext(UserContext);

    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState(false);
    const [role, setRole] = useState(userRolesOptions[0].value);    

    const { uid: userId } = useAppSelector(selectUserData);
    const { instanceId: organizationId } = useAppSelector(selectPartnerData);
    const teams = useAppSelector(selectTeamsForTeamSelector);
    const teamOptions: DropdownOption[] = teams.map(({ name: teamLabel, key: teamValue }) => ({
        label: teamLabel,
        value: teamValue,
    }));

    const [teamId, setTeamId] = useState(teamOptions[0]?.value|| '');

    const [sendUserInvitations, { loading }] = useSendEmailInviteMutation({
        onCompleted: () => {
            showNotification({
                message: `We've sent invitation to ${email}`,
                severity: 'success',
            });
        },
        onError: () => {
            showNotification({
                message: 'Unable to send email invitation',
                severity: 'error',
            });
        },
    });

    const validateEmail = (email: string) => {
        const isValidEmail = validateEmailAddress(email);

        if (!isValidEmail || isEmpty(email)) {
            setEmailError(true);
            return;
        }

        setEmailError(false);
    };

    const onEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const emailValue = event.target.value.trim();
        setEmail(emailValue);

        if (emailError) {
            validateEmail(emailValue);
        }
    };

    const onEmailBlur = () => {
        validateEmail(email);
    };

    const onDropdownChange = (event: SelectChangeEvent): void => {
        const selectedUserRole = event.target.value;

        setRole(selectedUserRole);
    };

    const handleEmailSend = async (resetValues: boolean) => {
        try {
            if (!email) {
                setEmailError(true);
                return;
            }

            const createInviteInput: InvitedUser[] = [
                {
                    email: email,
                    role: role as FirestoreUserRole,
                },
            ];

            const {
                data: { sendEmailInvite: invitesSent },
            } = await sendUserInvitations({
                variables: {
                    input: {
                        instanceId: organizationId,
                        teamId,
                        inviterId: userId,
                        invitations: createInviteInput,
                    },
                },
            });
            if (resetValues) {
                setEmail('');
                setRole(userRolesOptions[0].value);
            } else {
                setHeader({
                    title: UsersViewTitles.InviteUsers,
                    Icon: SendOutlined,
                });
            }
            addInvitation(invitesSent);
        } catch (error) {
            console.error(
                'something went wrong while sending an email invitation',
                error,
                error,
            );
        }
    };

    const handleTeamChange = (e: SelectChangeEvent) => {
        const teamId = e.target.value;
        setTeamId(teamId)
    };

    return (
        <Grid
            item
            xs={12}
            sx={{
                display: 'flex',
                justifyContent: 'center',
            }}
        >
            <StyledInputsContainer>
                <StyledSingleInputContainer>
                    <TextField
                        style={{ width: '100%' }}
                        ref={emailInputRef}
                        onBlur={onEmailBlur}
                        error={emailError}
                        label="Email Address"
                        helperText={
                            emailError
                                ? 'Please enter a valid email address.'
                                : 'We’ll wait while you head off to copy and paste'
                        }
                        required
                        type="email"
                        value={email}
                        onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                        ) => onEmailChange(event)}
                    />
                </StyledSingleInputContainer>
                <StyledSingleInputContainer>
                    <Dropdown
                        value={teamId}
                        options={teamOptions}
                        helperText="Which team will they be invited to?"
                        sx={{ width: '446px' }}
                        onChange={handleTeamChange}
                    />
                </StyledSingleInputContainer>
                <StyledSingleInputContainer style={{ marginBottom: '0px' }}>
                    <Dropdown
                        value={role}
                        options={userRolesOptions}
                        helperText="What level of access does this person have?"
                        sx={{ width: '446px' }}
                        onChange={(event: SelectChangeEvent) =>
                            onDropdownChange(event)
                        }
                    />
                </StyledSingleInputContainer>
                <SendEmailActions
                    disabled={emailError || loading}
                    onSendInvite={() => handleEmailSend(false)}
                    onSendAnother={() => handleEmailSend(true)}
                />
            </StyledInputsContainer>
        </Grid>
    );
};

export default SendEmailInvitation;
